/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { formatRecordText } from '@sportninja/common/features/competition/operations/utils/standingsByTeam'
import {
  OUTCOME_LABEL,
  RECORD_OUTCOMES,
  recordBarWidthPercent,
} from '@sportninja/common/features/competition/operations/utils/recordBarSegments'
import type { InDivisionRecord } from '@sportninja/common/features/competition/operations/types/standings.types'
import { SkeletonText } from 'src/components/ts-components/SkeletonBlock'
import { font, paragraphXSmall, weightMedium } from 'src/components/css'
import RecordBar, { EmptyRecordTrack } from '../division/RecordBar'

const translate = t as (key: string, options?: unknown) => string

/**
 * Cell width NOT available to the bar: AG Grid's own horizontal cell padding,
 * the record text (11 tabular chars at 12px), and the 10px gap between them.
 * The grid subtracts this from the record column's width to get the track the
 * bar draws into — see `recordTrackWidth` in DivisionTeamsGrid.
 */
export const RECORD_CELL_RESERVED_WIDTH = 34 + 10 + 78

interface RecordCellProps {
  teamName: string
  /** `null` when the team has played nothing, `undefined` when unknown. */
  record?: InDivisionRecord | null
  /**
   * Busiest team in the division, so a bar in this cell is scaled against the
   * same yardstick the Division Performance chart uses. Without it a one-game
   * team would fill the cell exactly like a forty-game team.
   */
  maxGamesPlayed: number
  isLoading: boolean
  /**
   * Track width in px, measured ONCE by the grid for the whole column. Every
   * row's track is the same width, so measuring per row would put 20-50
   * ResizeObservers on a large division — all firing, and all re-rendering, on
   * one panel resize. 0 means "not measured yet"; the bar is held back rather
   * than drawn at zero width.
   */
  trackWidth: number
}

/**
 * A team's in-division record inside the teams sheet: the same proportional
 * bar as the Division Performance chart, plus the record as text.
 *
 * The bar is the chart's `RecordBar` component unchanged — same segment order,
 * same colours, same scaling — so the two views cannot drift apart.
 */
const RecordCell = ({
  teamName,
  record,
  maxGamesPlayed,
  isLoading,
  trackWidth,
}: RecordCellProps) => {
  'use memo'

  if (isLoading) {
    return (
      <div css={wrapStyle}>
        <SkeletonText width='100%' />
      </div>
    )
  }

  // Undefined means this team has no standings entry at all — no bar and no
  // text, rather than a fabricated empty record.
  if (record === undefined) {
    return (
      <div css={wrapStyle}>
        <span css={emptyTextStyle}>{translate('common:notSet')}</span>
      </div>
    )
  }

  if (record === null) {
    return (
      <div css={wrapStyle}>
        <div css={trackStyle}>
          <EmptyRecordTrack teamName={teamName} />
        </div>
        <span css={emptyTextStyle}>{translate('common:noGames')}</span>
      </div>
    )
  }

  // Same phrasing as the chart's bar label, e.g. "9 Wins, 2 Losses".
  const label = RECORD_OUTCOMES.filter((outcome) => record[outcome])
    .map((outcome) => `${record[outcome]} ${OUTCOME_LABEL[outcome]}`)
    .join(', ')

  // Percent of the track, then resolved to px against the grid's single
  // measurement — so the bar never measures anything itself.
  const barWidth = Math.round(
    (recordBarWidthPercent(record.gamesPlayed, maxGamesPlayed) / 100) *
      trackWidth
  )

  return (
    <div css={wrapStyle}>
      <div css={trackStyle}>
        {trackWidth > 0 && (
          <RecordBar
            record={record}
            teamName={teamName}
            label={label}
            width={barWidth}
          />
        )}
      </div>
      <span css={recordTextStyle}>{formatRecordText(record)}</span>
    </div>
  )
}

const wrapStyle = css`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  /* Fills the cell so the centring above has the row's full height to work
     against — without it the renderer's box only wraps its own content and
     the bar rides high against the cell's top padding. */
  height: 100%;
  min-width: 0;
`

const trackStyle = css`
  flex: 1;
  min-width: 0;
`

const recordTextStyle = css`
  ${font.body}
  ${paragraphXSmall}
  ${weightMedium}
  flex: none;
  color: ${colors.NEUTRAL_50};
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.03em;
  /* The bar takes the slack, so the record string never wraps to a second
     line inside a fixed-height grid row. */
  white-space: nowrap;
`

const emptyTextStyle = css`
  ${font.body}
  ${paragraphXSmall}
  ${weightMedium}
  flex: none;
  color: ${colors.NEUTRAL_100};
  font-style: italic;
`

export default RecordCell
